/**
 * Attribution Filters Component
 *
 * Multi-select dropdowns for filtering analytics by:
 * - Traffic sources
 * - Referrer categories
 * - UTM campaigns
 *
 * @module components/analytics/attribution-filters
 */

import { X, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { Badge } from '@/client/components/ui/badge';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface AttributionFiltersProps {
  sources: FilterOption[];
  referrerCategories: FilterOption[];
  utmCampaigns: FilterOption[];
  selectedSources: string[];
  selectedCategories: string[];
  selectedCampaigns: string[];
  onSourcesChange: (sources: string[]) => void;
  onCategoriesChange: (categories: string[]) => void;
  onCampaignsChange: (campaigns: string[]) => void;
  onClearAll: () => void;
  disabled?: boolean;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const hasSelection = selected.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant={hasSelection ? 'default' : 'outline'}
          size="sm"
          className="rounded-xl gap-1.5 h-8"
        >
          {label}
          {hasSelection && (
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
              {selected.length}
            </Badge>
          )}
          <ChevronDown className="size-3.5 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 max-h-64 overflow-y-auto">
        {options.length === 0 ? (
          <div className="px-2 py-4 text-sm text-muted-foreground text-center">
            No options available
          </div>
        ) : (
          options.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="truncate">{option.label}</span>
              <div className="flex items-center gap-2">
                {option.count !== undefined && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {option.count.toLocaleString()}
                  </span>
                )}
                {selected.includes(option.value) && <Check className="size-4 text-primary" />}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AttributionFilters({
  sources,
  referrerCategories,
  utmCampaigns,
  selectedSources,
  selectedCategories,
  selectedCampaigns,
  onSourcesChange,
  onCategoriesChange,
  onCampaignsChange,
  onClearAll,
  disabled,
}: AttributionFiltersProps) {
  const totalFilters =
    selectedSources.length + selectedCategories.length + selectedCampaigns.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Source Filter */}
      {sources.length > 0 && (
        <MultiSelectDropdown
          label="Source"
          options={sources}
          selected={selectedSources}
          onChange={onSourcesChange}
          disabled={disabled}
        />
      )}

      {/* Category Filter */}
      {referrerCategories.length > 0 && (
        <MultiSelectDropdown
          label="Category"
          options={referrerCategories}
          selected={selectedCategories}
          onChange={onCategoriesChange}
          disabled={disabled}
        />
      )}

      {/* Campaign Filter */}
      {utmCampaigns.length > 0 && (
        <MultiSelectDropdown
          label="Campaign"
          options={utmCampaigns}
          selected={selectedCampaigns}
          onChange={onCampaignsChange}
          disabled={disabled}
        />
      )}

      {/* Clear All Button */}
      {totalFilters > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="rounded-xl h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          disabled={disabled}
        >
          <X className="size-3.5" />
          Clear ({totalFilters})
        </Button>
      )}
    </div>
  );
}
